import { motion } from "framer-motion";
import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";

import { Seo } from "@/components/seo";
import { useTheme } from "@/components/theme-provider";
import { JoinBreakoutSessionModal } from "@/features/breakout-sessions/components/join-breakout-session.modal";
import { ReturnToParentSessionModal } from "@/features/breakout-sessions/components/return-to-parent-session.modal";
import { ModerationDrawer } from "@/features/call-settings/components/moderation-drawer";
import { SettingsDrawer } from "@/features/call-settings/components/settings-drawer";
import { ChatDrawer } from "@/features/chat/components/chat-drawer";
import { useSubscribeToClosedCaptioning } from "@/features/closed-captioning/api/subscribe-to-closed-captioning";
import { ClosedCaptionsDisplay } from "@/features/closed-captioning/components/closed-captions-display";
import { useSubscribeToParticipants } from "@/features/participants/api/subscribe-to-participants";
import { ParticipantsDrawer } from "@/features/participants/components/participants-drawer";
import { useAutoLowerHand } from "@/features/raise-hand/api/auto-lower-hand";
import { useGetSessionByKey } from "@/features/session/api/get-session-by-key";
import { ActionBar } from "@/features/vidyo-call/components/action-bar";
import { CallHeader } from "@/features/vidyo-call/components/call-header";
import CallRenderScreen from "@/features/vidyo-call/components/call-render-screen";
import { useJoinRoom } from "@/features/vidyo-call/components/join-room";
import { SettingsBar } from "@/features/vidyo-call/components/settings-bar";
import "@/features/vidyo-call/style/vidyo-call.css";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { useManageDevicesStatus } from "@/features/vidyo-devices/api/manage-devices-status";
import { useScreenShareGuard } from "@/features/vidyo-screen-share/api/share-screen";
import { cn } from "@/lib/utils";

export function CallPage() {
  const { key } = useParams();
  const isSettingsDrawerOpen = useVidyoStore((s) => s.settingsDrawer.isOpen);
  const isParticipantsDrawerOpen = useVidyoStore(
    (s) => s.participantsDrawer.isOpen,
  );
  const { theme } = useTheme();
  const roomKey = useVidyoStore((store) => store.roomKey);
  const isModerationDrawerOpen = useVidyoStore(
    (s) => s.moderationDrawer.isOpen,
  );
  const isChatDrawerOpen = useVidyoStore((s) => s.chatDrawer.isOpen);
  const isVidyoConnected = useVidyoStore((store) => store.isVidyoConnected);

  useSubscribeToClosedCaptioning();

  /**
   * we need to set the session id, so we can perform moderation actions.
   * This is also set up on the pre-call page, but we need to set it here
   * because if you connect to a breakout room, you will not go through the
   * pre-call screen.
   */
  //TODO: handle error when session is not found
  const { data: session, isSuccess: isSessionSuccess } = useGetSessionByKey(
    key ?? "",
  );

  const setSession = useVidyoStore((state) => state.setSession);
  const setClosedCaptioningEnabled = useVidyoStore(
    (state) => state.closedCaptioning.setEnabled,
  );
  useEffect(() => {
    if (isSessionSuccess) {
      setSession({
        id: session.id,
        parentSession: session.parentSession,
      });
      setClosedCaptioningEnabled(session.isCCEnabled);
    }
  }, [
    isSessionSuccess,
    session?.id,
    session?.parentSession,
    setSession,
    setClosedCaptioningEnabled,
    session?.isCCEnabled,
  ]);

  useManageDevicesStatus();
  useSubscribeToParticipants();
  useAutoLowerHand();
  useJoinRoom();
  useScreenShareGuard();
  /**
   * Set the dark mode for call page.
   * We dont set with the useTheme hook but manually adding the class.
   * But if the theme is already dark, we dont need to set it again.
   */
  useEffect(() => {
    if (theme === "dark") {
      return;
    }

    document.body.classList.add("dark");

    return () => {
      document.body.classList.remove("dark");
    };
  }, [theme]);

  /**
   * If the room key is not set, we redirect to the pre-call page
   * This can happen if user hit refresh on the call screen. We dont want them
   * to rejoin instantly, we want them to go through the pre-call screen again.
   */
  if (!roomKey) {
    return <Navigate to={`/r/${key}`} />;
  }

  const isSidebarOpen =
    isSettingsDrawerOpen ||
    isParticipantsDrawerOpen ||
    isModerationDrawerOpen ||
    isChatDrawerOpen;

  const drawerPlaceholder = 320 + 16 + 16;
  return (
    <div className="dark flex h-full w-full flex-col bg-background">
      <Seo title={isSessionSuccess ? session?.name : "Session"} />
      <motion.div
        className="w-full transition-transform"
        initial={{ paddingRight: 0 }}
        animate={{
          paddingRight: isSidebarOpen ? drawerPlaceholder : 0,
        }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <CallHeader />
      </motion.div>
      <div
        className="flex h-full"
        style={
          {
            "--drawer-placeholder-width": `${drawerPlaceholder}px`,
          } as React.CSSProperties
        }
      >
        <motion.div
          className="w-full transition-transform"
          initial={{ paddingRight: 0 }}
          animate={{
            paddingRight: isSidebarOpen ? drawerPlaceholder : 0,
          }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <CallRenderScreen />

          {!isVidyoConnected && (
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center",
                isSidebarOpen && "pr-[var(--drawer-placeholder-width)]",
              )}
            >
              <div className="text-lg text-muted">Connecting to call...</div>
            </div>
          )}
        </motion.div>
        <SettingsDrawer />
        <ParticipantsDrawer />
        <ChatDrawer />
        <ModerationDrawer />
        <JoinBreakoutSessionModal />
        <ReturnToParentSessionModal />
      </div>
      <ClosedCaptionsDisplay />

      <div
        className={cn(
          "dark relative flex h-[var(--action-bar-height)] w-full items-center bg-background px-6 py-6",
          "justify-center", // on small screens controls are in the center
          "lg:justify-between", // on medium screens controls are on the left and right
          "2xl:mx-auto 2xl:justify-center", // on large screens controls are in the center
        )}
      >
        <div className="w-full xl:w-auto 2xl:absolute">
          <ActionBar />
        </div>
        {/* Hide the settings bar on small screens, we add those controls in the action bar */}
        <div className="hidden w-full justify-end lg:flex">
          <SettingsBar />
        </div>
      </div>
    </div>
  );
}
